import React, { useState, useEffect, forwardRef } from 'react';
import { useSelector } from "react-redux";
import moment from 'moment-timezone';
import ReactHighcharts from 'highcharts-react-official';
import Highcharts from 'highcharts';
import HighchartsDrilldown from 'highcharts/modules/drilldown.src';
import HighchartsExport from 'highcharts/modules/exporting';
import HighchartsNoData from 'highcharts/modules/no-data-to-display';
import { groupBy, isPlannedOrTodate } from '../../../helpers/common';
import { DISPLAY_DATE_FORMAT2 } from '../../../constants';
import { Unit, formatNumber } from '../../../helpers/unit';

HighchartsDrilldown(Highcharts);
HighchartsExport(Highcharts);
HighchartsNoData(Highcharts);

export const getFileName = props => {
  const { farmName, search: { range, custom } } = props;

  let dateRange = '';
  if (isPlannedOrTodate(range)) dateRange = range;
  else {
    const from = moment.utc(custom.from);
    const to = moment.utc(custom.to);
    if (to.diff(from, "hours") <= 24) dateRange = `${from.format(DISPLAY_DATE_FORMAT2)}`;
    else dateRange = `from ${from.format(DISPLAY_DATE_FORMAT2)} to ${to.format(DISPLAY_DATE_FORMAT2)}`;
  }

  return `${farmName} loads ${dateRange}`;
};

const DrillDownGraph = forwardRef((props, chartRef) => {
  const farmName = useSelector(state => state.farm.currentFarm.farm_name);
  const namesById = useSelector(state => state.dashboard.loads.namesById);
  const loadHistory = useSelector(state => state.dashboard.loads.loadHistory);
  const loadSequenceData = useSelector(state => state.dashboard.loads.loadHistory.loadSequenceData);
  const chartViewType = useSelector(state => state.dashboard.loads.chartViewType);
  const viewTypes = useSelector(state => state.dashboard.loads.viewTypes);
  const view = useSelector(state => state.dashboard.loads.viewTypes.filter(e => e.id === state.dashboard.loads.viewId)[0]);
  const subView = useSelector(state => state.dashboard.loads.viewTypes.filter(e => e.id === state.dashboard.loads.subViewId)[0]);
  const subViewId = useSelector(state => state.dashboard.loads.subViewId);
  const settings = useSelector(state => state.user.settings);
  const { range, custom } = useSelector(state => state.analyze.searchParams);
  const selectedPlanType = useSelector(state => state.plan.selectedPlanType);
  const chartProps = { farmName: farmName, search: { range: range, custom: custom } }
  const backgroundColor = { dark: "#3a3a4f", default: "#ffffff" };
  const titleFontColor = { dark: "#ffffff", default: "#333333" };
  const [drilldownId, setDrillDownId] = useState(null);
  const [chart, setChart] = useState(null);
  const [mainLoads, setMainLoads] = useState({});
  const [mainNameById, setMainNamesById] = useState({});
  const unit = Unit.getInstance();

  const getTotalCapacity = (list) => {
    const totalCapacity = list.reduce((capacity, item) => capacity + unit.toWeight(item.loadCapacity, 2), 0);
    return unit.toFixedOf(totalCapacity, 2);
  }

  const [options, setOptions] = useState({
    credits: {
      enabled: false
    },
    lang: {
      loading: "Loading...",
      noData: "No data found"
    },
    noData: {
      style: {
        fontSize: '15px',
        color: '#aca7a7'
      }
    },
    loading: {
      style: {
        fontSize: '15px',
        color: '#2a2929',
        marginTop: "21px"
      }
    },
    chart: {
      type: "column",
      backgroundColor: backgroundColor.default
    },
    colors: ['#2b908f', '#90ee7e', '#f45b5b', '#7798BF', '#aaeeee', '#ff0066', '#eeaaee', '#55BF3B', '#DF5353', '#7798BF', '#aaeeee'],
    title: {
      text: null,
      style: {
        fontSize: "17px",
        fontColor: titleFontColor.default
      }
    },
    accessibility: {
      announceNewData: {
        enabled: true
      }
    },
    xAxis: {
      type: 'category'
    },
    yAxis: {
      title: {
        text: chartViewType === 1 ? 'Loads' : `Weight (${settings.weightShortName})`
      }
    },
    legend: {
      enabled: false
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          format: `{point.y}`
        }
      },
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<span style="color:{point.color}">{point.name}</span><br> ' +
            `<span>Loads</span>: <b> {point.count} ({point.weight}  ${settings.weightShortName})</b> <br />`,
          style: {
            fontWeight: "normal",
            color: "#e2e0e0e8",
            fontSize: "13px",
            backgroundColor: "transparent",
            letterSpacing: "1px",
            textOutline: false
          }
        }
      }
    },
    tooltip: {
      enabled: true,
      headerFormat: undefined,
      pointFormat: '<span style="color:{point.color}">{point.name}</span>' +
        '<br><span>Loads</span>: <b>{point.count}</b><br />' +
        '<br><span>Weight</span>: <b>{point.weight}</b> {point.unit}<br/>'
    },
    series: [{
      name: "Loads",
      colorByPoint: true,
      data: []
    }],
    drilldown: {
      series: []
    },
    exporting: {
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'separator',
            'downloadPNG',
            'downloadJPEG',
            'downloadPDF',
            'downloadSVG',
            'downloadCSV'
          ]
        }
      }
    },
  });

  useEffect(() => {
    const element = document.getElementById("dashboard-drill-down-chart");
    if (element && element.children && element.children.length !== 0)
      element.children[0].setAttribute("class", "drill-down-highchart");
  }, []);

  useEffect(() => {
    if (!chart || !chart.series) return;
    if (loadHistory.isRequesting) {
      chart.series[0].remove(true);
      chart.chartBackground.css({ color: backgroundColor.default });
      chart.hideNoData();
      chart.showLoading();
    }
  }, [loadHistory.isRequesting, chart])

  useEffect(() => {
    setDrillDownId(null);
    if (loadHistory.isRequesting) return;
    let loadData = [];
    if (selectedPlanType === 2) {
      loadData = loadSequenceData;
    } else {
      loadData = loadHistory.payload;
    }
    const optimize = getOptimizedLoads(groupBy(view.keyName)(loadData), view);
    const mainLoads = optimize.optimizedLoads;
    const mainNameById = optimize.nameById;
    setMainLoads(mainLoads);
    setMainNamesById(mainNameById);
  }, [view.id, loadHistory.payload, loadSequenceData]);

  useEffect(() => {
    const data = getDrillUpData(mainLoads, mainNameById, chartViewType);
    const opt = {
      ...options,
      title: {
        ...options.title,
        text: `${view.name} -> ${subView.name}`
      },
      exporting: {
        ...options.exporting,
        filename: getFileName(chartProps)
      },
      chart: {
        ...options.chart,
        events: {
          drilldown: (e) => {
            e.target.chartBackground.css({ color: backgroundColor.dark });
            e.target.title.css({ color: titleFontColor.dark });
            setDrillDownId(e.point.id);
            const data = getDrillDownData(e.point.id, mainLoads, mainNameById, window.__viewType, window.__subViewId);
            e.target.addSeriesAsDrilldown(e.point, data);
          },
          drillup: (e) => {
            e.target.chartBackground.css({ color: backgroundColor.default });
            e.target.title.css({ color: titleFontColor.default });
            setDrillDownId(null);
            const data = getDrillUpData(mainLoads, mainNameById, window.__viewType);
            e.target.series[1].setData(data);
          },
          load: function () {
            setChart(this)
          }
        }
      },
      series: [{
        ...options.series[0],
        name: view.name,
        data: data
      }],
      drilldown: {
        series: []
      }
    };

    setOptions(opt);
  }, [mainLoads]);

  useEffect(() => {
    if (!chart) return;
    chart.yAxis[0].axisTitle.attr({
      text: chartViewType === 1 ? 'Loads' : `Weight (${settings.weightShortName})`
    });

    if (drilldownId) return;
    const data = getDrillUpData(mainLoads, mainNameById, chartViewType);
    chart.series[0].update({ data: data });
  }, [chartViewType])

  useEffect(() => {
    window.__subViewId = subViewId;
    window.__viewType = chartViewType;

    if (!chart) return;
    chart.setTitle({ text: `${view.name} -> ${subView.name}` });

    if (!drilldownId) return;
    const data = getDrillDownData(drilldownId, mainLoads, mainNameById, chartViewType, subViewId);
    chart.update({ series: data });
  }, [subViewId, chartViewType]);

  const getOptimizedLoads = (groupedByLoads, view) => {
    const nameById = namesById;

    let optimizedLoads = {},
      combinedCount = 0,
      groupedCount = Object.keys(groupedByLoads).length;

    if (view.keyName === "targetEquipmentGroupId") {
      if (groupedCount >= 3000) combinedCount = 20;
      else if (groupedCount >= 200) combinedCount = 10;
    }

    if (combinedCount > 0) {
      for (const id in groupedByLoads) {
        if (groupedByLoads[id].length <= combinedCount) {
          if (!optimizedLoads["otherTruck"]) optimizedLoads["otherTruck"] = [];
          optimizedLoads["otherTruck"] = [...optimizedLoads["otherTruck"], ...groupedByLoads[id]];
          nameById["otherTruck"] = `Other transport (<= ${combinedCount} loads)`;
        }
        else optimizedLoads[id] = groupedByLoads[id];
      }
    }
    else optimizedLoads = groupedByLoads;

    return { optimizedLoads, nameById };
  };

  const getDrillUpData = (mainLoads, mainNameById, chartViewType) => {
    const data = Object.keys(mainLoads).map(id => {
      const totalWeight = getTotalCapacity(mainLoads[id]);
      return ({
        id,
        name: mainNameById[id],
        y: chartViewType === 1 ? mainLoads[id].length : totalWeight,
        drilldown: true,
        count: mainLoads[id].length,
        weight: formatNumber(totalWeight),
        unit: settings.weightShortName
      })
    }).sort((a, b) => b.y - a.y)

    return data;
  }

  const getDrillDownData = (drilldownId, mainLoads, mainNameById, chartViewType, subViewId) => {
    const subView = viewTypes.filter(e => e.id === subViewId)[0];
    const optimize = getOptimizedLoads(groupBy(subView.keyName)(mainLoads[drilldownId]), subView);
    const subLoads = optimize.optimizedLoads;
    const subNameById = optimize.nameById;

    const data = {
      id: drilldownId,
      type: 'pie',
      name: mainNameById[drilldownId],
      colorByPoint: true,
      data: Object.keys(subLoads)
        .map(subId => {
          const totalWeight = getTotalCapacity(subLoads[subId]);
          return ({
            name: subNameById[subId],
            y: chartViewType === 1 ? subLoads[subId].length : totalWeight,
            count: subLoads[subId].length,
            weight: formatNumber(totalWeight),
            unit: settings.weightShortName
          })
        }).sort((a, b) => b.y - a.y)
    }
    return data
  }

  return <ReactHighcharts ref={chartRef} immutable={true} highcharts={Highcharts} options={options} />;
});

export default DrillDownGraph;
